import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CalendarBlank } from '@phosphor-icons/react';

const PALETTE = ['#E04B2A', '#2B6EF0', '#E5A100', '#10A882', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];
const fmt = (n) => {
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n?.toLocaleString('en-IN') || '0';
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    api.get('/api/analytics/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/api/calendar/upcoming').then(r => setUpcomingEvents((r.data.events || []).slice(0, 5))).catch(console.error);
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground font-mono">loading...</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Failed to load</div>;

  const tt = {
    fontSize: 11,
    borderRadius: 8,
    fontFamily: 'DM Sans',
    background: theme === 'dark' ? '#1C1C1C' : '#fff',
    border: `1px solid ${theme === 'dark' ? '#2E2E2E' : '#E3E0DB'}`,
    color: theme === 'dark' ? '#EDEBE6' : '#191919',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  };
  const ax = theme === 'dark' ? '#666' : '#999';

  const kpis = [
    { label: 'Total Leads', value: data.total_leads, accent: '#2B6EF0' },
    { label: 'Qualified', value: data.qualified_leads, accent: '#8B5CF6' },
    { label: 'Enrolled', value: data.enrolled_students, accent: '#10A882' },
    { label: 'Conversion', value: `${data.conversion_rate}%`, accent: '#10A882' },
    { label: 'Revenue', value: `Rs.${fmt(data.revenue_collected)}`, accent: '#E5A100' },
    { label: 'Outstanding', value: `Rs.${fmt(data.outstanding_dues)}`, accent: '#E04B2A' },
  ];

  return (
    <div data-testid="dashboard-page" className="stagger-in">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Admitrix Academy Group &mdash; overview</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="kpi-cards">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border border-border rounded-xl shadow-none hover:shadow-md transition-shadow card-lift overflow-hidden">
            <CardContent className="p-4 relative">
              <div className="absolute top-0 left-0 w-full h-[3px] rounded-t-xl" style={{ backgroundColor: kpi.accent }} />
              <p className="font-display text-2xl font-extrabold text-foreground mt-1">{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.08em] font-semibold mt-1">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        <Card className="lg:col-span-3 border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-5">Leads by Source</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.leads_by_source} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
                <XAxis dataKey="source" tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} />
                <Tooltip contentStyle={tt} />
                <Bar dataKey="count" fill="#E04B2A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-5">Pipeline</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.leads_by_stage} dataKey="count" nameKey="stage" cx="50%" cy="50%" outerRadius={72} innerRadius={42} paddingAngle={2} strokeWidth={0}>
                  {data.leads_by_stage.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tt} formatter={(v, n) => [v, n.replace('_', ' ')]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {data.leads_by_stage.map((s, i) => (
                <span key={s.stage} className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono">
                  <span className="w-2 h-2 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                  {s.stage?.replace('_', ' ')} ({s.count})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card className="border border-border rounded-xl shadow-none mb-6" data-testid="upcoming-events-card">
        <CardContent className="p-5">
          <h3 className="font-display font-bold text-sm text-foreground mb-4 flex items-center gap-2">
            <CalendarBlank size={16} weight="bold" /> Upcoming Events
          </h3>
          <div className="space-y-0">
            {upcomingEvents.map((evt) => (
              <div key={evt._id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#E5A10015', color: '#E5A100' }}>
                  <CalendarBlank size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-foreground font-medium truncate">{evt.summary}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                    {evt.start_time ? new Date(evt.start_time).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    {evt.type ? ` \u00b7 ${evt.type.replace('_', ' ')}` : ''}
                    {evt.lead_id ? ` \u00b7 Lead` : ''}
                  </p>
                </div>
                {evt.google_link && (
                  <a href={evt.google_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline font-mono shrink-0">
                    Open
                  </a>
                )}
              </div>
            ))}
            {upcomingEvents.length === 0 && (
              <p className="text-xs text-muted-foreground py-4 text-center font-mono">No upcoming events this week</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Counselor Workload */}
      {data.counselor_performance?.length > 0 && (
        <Card className="border border-border rounded-xl shadow-none mb-6" data-testid="counselor-workload-card">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Counselor Workload</h3>
            <div className="grid grid-cols-4 gap-2 mb-3 text-[9px] text-muted-foreground uppercase tracking-wider font-bold px-1">
              <span>Counselor</span>
              <span className="text-center">Active Leads</span>
              <span className="text-center">Enrolled</span>
              <span className="text-center">Conversion</span>
            </div>
            <div className="space-y-0">
              {(() => {
                const maxLeads = Math.max(...data.counselor_performance.map(c => c.total_leads || 0), 1);
                return data.counselor_performance.slice(0, 8).map((c) => {
                  const pct = ((c.total_leads || 0) / maxLeads) * 100;
                  const barColor = pct > 80 ? '#EF4444' : pct > 50 ? '#E5A100' : '#10A882';
                  return (
                    <div key={c.counselor_id} className="py-2.5 border-b border-border last:border-0">
                      <div className="grid grid-cols-4 gap-2 items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground font-display shrink-0">
                            {c.name?.charAt(0)}
                          </div>
                          <span className="text-[12px] text-foreground font-medium truncate">{c.name}</span>
                        </div>
                        <span className="text-[12px] text-foreground font-mono text-center font-semibold">{c.total_leads}</span>
                        <span className="text-[12px] font-mono text-center font-semibold" style={{ color: '#10A882' }}>{c.enrolled}</span>
                        <span className="text-[12px] text-foreground font-mono text-center font-semibold">{c.conversion_rate}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-sm bg-secondary overflow-hidden mx-1">
                        <div className="h-full rounded-sm transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Counselors + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Counselor Performance</h3>
            <div className="space-y-0">
              {data.counselor_performance?.slice(0, 6).map((c, i) => (
                <div key={c.counselor_id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[10px] text-muted-foreground font-mono w-4">{i + 1}</span>
                    <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground font-display">
                      {c.name?.charAt(0)}
                    </div>
                    <span className="text-[13px] text-foreground font-medium">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span className="text-muted-foreground">{c.total_leads}</span>
                    <span style={{ color: '#10A882' }}>{c.enrolled}</span>
                    <span className="text-foreground font-semibold">{c.conversion_rate}%</span>
                  </div>
                </div>
              ))}
              {(!data.counselor_performance || data.counselor_performance.length === 0) && (
                <p className="text-xs text-muted-foreground py-4 text-center">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Recent Activity</h3>
            <div className="space-y-0">
              {data.recent_activities?.slice(0, 8).map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0`} style={{
                    backgroundColor: a.type === 'stage_change' ? '#2B6EF0' : a.type === 'call' ? '#10A882' : a.type === 'note' ? '#808080' : '#8B5CF6'
                  }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] text-foreground truncate">{a.content}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {a.user_name} &middot; {a.type?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              ))}
              {(!data.recent_activities || data.recent_activities.length === 0) && (
                <p className="text-xs text-muted-foreground py-4 text-center">No activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
